// Phase H.12a — Frontend export helpers (HTML & Markdown).
//
// HTML: wraps the un-substituted template body in a minimal standalone
// HTML document so the user can open it in any browser. Merge tags remain
// as plain `{{tag}}` text (the file is for proofing/authoring, not for
// customer-facing rendering — that path goes through the backend).
//
// Markdown: converts the body via Turndown. Custom rules handle our merge
// tag chips, page breaks, section breaks, page-number atoms, and tables.

import TurndownService from "turndown";

function sanitizeFilename(name) {
    return (name || "template")
        .replace(/[^A-Za-z0-9._-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 80) || "template";
}

function downloadBlob(filename, mime, content) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 0);
}

// Minimal HTML shell so the exported file opens cleanly in a browser.
// Inline-styles only (no external CSS) so the file is self-contained.
function buildHtmlShell({ title, headerHtml, bodyHtml, footerHtml }) {
    const safeTitle = String(title || "Template").replace(/[<>]/g, "");
    const sections = [];
    if (headerHtml && headerHtml.trim()) {
        sections.push(`<header class="doc-header">${headerHtml}</header>`);
    }
    sections.push(`<main class="doc-body">${bodyHtml || ""}</main>`);
    if (footerHtml && footerHtml.trim()) {
        sections.push(`<footer class="doc-footer">${footerHtml}</footer>`);
    }
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${safeTitle}</title>
<style>
  body { font-family: -apple-system, system-ui, "Segoe UI", Helvetica, Arial, sans-serif; color: #0f172a; max-width: 8.5in; margin: 1in auto; padding: 0 0.5in; line-height: 1.55; font-size: 11pt; }
  h1 { font-size: 22pt; margin: 0 0 12pt; }
  h2 { font-size: 16pt; margin: 16pt 0 6pt; color: #1e3a8a; }
  h3 { font-size: 13pt; margin: 12pt 0 4pt; }
  p  { margin: 4pt 0 8pt; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #e2e8f0; padding: 4pt 6pt; text-align: left; }
  hr.page-break { page-break-before: always; border: none; height: 0; }
  hr.section-break { border: none; border-top: 1px solid #94a3b8; margin: 1.5em 0; }
  .doc-header, .doc-footer { color: #64748b; font-size: 9.5pt; }
  .doc-header { border-bottom: 1px solid #e2e8f0; padding-bottom: 6pt; margin-bottom: 12pt; }
  .doc-footer { border-top: 1px solid #e2e8f0; padding-top: 6pt; margin-top: 18pt; text-align: center; }
  .page-number, .page-number-total { font-variant-numeric: tabular-nums; }
</style>
</head>
<body>
${sections.join("\n")}
</body>
</html>`;
}

export function exportTemplateAsHtml({ name, headerHtml, bodyHtml, footerHtml }) {
    const html = buildHtmlShell({ title: name, headerHtml, bodyHtml, footerHtml });
    downloadBlob(`${sanitizeFilename(name)}.html`, "text/html;charset=utf-8", html);
}

// ---- Turndown configuration ----

let _td = null;
function getTurndown() {
    if (_td) return _td;
    _td = new TurndownService({
        headingStyle: "atx",         // # heading
        codeBlockStyle: "fenced",    // ```code```
        bulletListMarker: "-",
        emDelimiter: "_",
        strongDelimiter: "**",
        linkStyle: "inlined",
        hr: "---",
    });

    // Strip the merge-tag chip wrapper down to plain `{{path}}` text.
    _td.addRule("mergeTag", {
        filter: (node) =>
            node.nodeType === 1 && // element
            node.nodeName === "SPAN" &&
            node.getAttribute("data-merge-tag"),
        replacement: (_, node) => `{{${node.getAttribute("data-merge-tag")}}}`,
    });

    // Page break → markdown `---` rule isn't right (that's an HR). Use a
    // visible marker so the markdown round-trip is lossless on intent.
    _td.addRule("pageBreak", {
        filter: (node) =>
            node.nodeName === "HR" &&
            ((node.getAttribute("class") || "").includes("page-break") ||
                /page-break/.test(node.getAttribute("style") || "")),
        replacement: () => "\n\n<!-- page-break -->\n\n",
    });

    // Section break → render as a Markdown HR with a comment so the intent
    // is obvious on re-import.
    _td.addRule("sectionBreak", {
        filter: (node) =>
            node.nodeName === "HR" &&
            (node.getAttribute("class") || "").includes("section-break"),
        replacement: () => "\n\n--- <!-- section-break -->\n\n",
    });

    // Page-number atoms — keep as inline placeholders in Markdown.
    _td.addRule("pageNumber", {
        filter: (node) =>
            node.nodeName === "SPAN" &&
            (node.getAttribute("class") || "").includes("page-number") &&
            !(node.getAttribute("class") || "").includes("page-number-total"),
        replacement: () => "{{page_number}}",
    });
    _td.addRule("pageNumberTotal", {
        filter: (node) =>
            node.nodeName === "SPAN" &&
            (node.getAttribute("class") || "").includes("page-number-total"),
        replacement: () => "{{page_number_total}}",
    });

    // Signature line block (H.11).
    _td.addRule("signatureLine", {
        filter: (node) =>
            node.nodeName === "DIV" &&
            (node.getAttribute("class") || "").includes("signature-line"),
        replacement: (_, node) => {
            const lbl = node.querySelector(".sig-label")?.textContent?.trim() || "Signature";
            return `\n\n______________________________\n${lbl}\n\n`;
        },
    });

    // Phase H.12b — Footnote chips. Use the standard Markdown footnote
    // syntax (`[^N]` at call site + `[^N]: content` at the bottom). The
    // bottom block is accumulated in `_td.__fn_bottom` and flushed in
    // `exportTemplateAsMarkdown`.
    _td.addRule("footnote", {
        filter: (node) =>
            node.nodeName === "SPAN" &&
            (node.getAttribute("class") || "").includes("footnote") &&
            node.hasAttribute("data-fn-content"),
        replacement: (_, node) => {
            const list = _td.__fn_bottom = _td.__fn_bottom || [];
            const content = node.getAttribute("data-fn-content") || "";
            list.push(content);
            return `[^${list.length}]`;
        },
    });

    // Phase H.12b — Endnote chips. Accumulated separately so we can emit a
    // dedicated "## Endnotes" section under the body.
    _td.addRule("endnote", {
        filter: (node) =>
            node.nodeName === "SPAN" &&
            (node.getAttribute("class") || "").includes("endnote") &&
            node.hasAttribute("data-en-content"),
        replacement: (_, node) => {
            const list = _td.__en_bottom = _td.__en_bottom || [];
            const content = node.getAttribute("data-en-content") || "";
            list.push(content);
            return `[${list.length}]`;
        },
    });

    // Tables — Turndown ships a tables plugin, but we don't want to pull
    // another dependency. Fall back to a simple GitHub-flavoured rendering.
    _td.addRule("table", {
        filter: "table",
        replacement: (_, node) => {
            const rows = Array.from(node.querySelectorAll("tr"));
            if (rows.length === 0) return "";
            const headerCells = Array.from(rows[0].querySelectorAll("th,td"))
                .map((c) => (c.textContent || "").trim().replace(/\|/g, "\\|"));
            const bodyRows = rows.slice(1).map((tr) =>
                Array.from(tr.querySelectorAll("td,th"))
                    .map((c) => (c.textContent || "").trim().replace(/\|/g, "\\|"))
            );
            const lines = [];
            lines.push("| " + headerCells.join(" | ") + " |");
            lines.push("| " + headerCells.map(() => "---").join(" | ") + " |");
            bodyRows.forEach((r) => lines.push("| " + r.join(" | ") + " |"));
            return "\n\n" + lines.join("\n") + "\n\n";
        },
    });

    return _td;
}

export function exportTemplateAsMarkdown({ name, headerHtml, bodyHtml, footerHtml }) {
    const td = getTurndown();
    // Phase H.12b — reset per-export footnote / endnote accumulators so a
    // second click of the Markdown button doesn't double-count.
    td.__fn_bottom = [];
    td.__en_bottom = [];
    const parts = [];
    parts.push(`# ${name || "Template"}\n`);
    if (headerHtml && headerHtml.trim()) {
        parts.push("<!-- header -->");
        parts.push(td.turndown(headerHtml));
        parts.push("<!-- /header -->\n");
    }
    parts.push(td.turndown(bodyHtml || ""));
    if (footerHtml && footerHtml.trim()) {
        parts.push("\n<!-- footer -->");
        parts.push(td.turndown(footerHtml));
        parts.push("<!-- /footer -->");
    }
    // Phase H.12b — flush the footnote / endnote tails.
    if (td.__fn_bottom.length) {
        parts.push("");
        td.__fn_bottom.forEach((c, i) => {
            parts.push(`[^${i + 1}]: ${c}`);
        });
    }
    if (td.__en_bottom.length) {
        parts.push("\n## Endnotes\n");
        td.__en_bottom.forEach((c, i) => {
            parts.push(`${i + 1}. ${c}`);
        });
    }
    const md = parts.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
    downloadBlob(`${sanitizeFilename(name)}.md`, "text/markdown;charset=utf-8", md);
}
